import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

/**
 * Iter-196.QQ — Portal-rendered tooltip.
 *
 * Why this exists
 * ---------------
 * Pre-iter-196.QQ, several in-table tooltips (ScoreHoverCard,
 * WhyRankTooltip, the PartnerRateTable "compare hint", the
 * InteractiveUSMap state hover, etc.) rendered as `position:absolute`
 * children of a `<td>`. Any ancestor with `overflow-x-auto` (which
 * every responsive table needs to handle narrow viewports) clipped the
 * popover so the hover content was effectively invisible — users could
 * see the info dot but not the explanation.
 *
 * `PortalTooltip` solves this by rendering the popover into
 * `document.body` via `createPortal`, escaping every parent overflow
 * context. Position is computed against the trigger's
 * `getBoundingClientRect()` and recomputed on resize/scroll. The
 * popover flips above the trigger when there isn't enough room below,
 * and clamps horizontally to the viewport so it never disappears off-
 * screen on narrow phones.
 *
 * Design contract
 * ---------------
 *   - Hover OR focus opens. Click toggles. Click-outside / Escape
 *     closes (matches SourcesPopover semantics).
 *   - Trigger is a small `<button>` carrying the user-provided
 *     `data-testid`; the popover panel carries `${testid}-panel`.
 *   - `panel` prop renders the contents inside the popover so callers
 *     keep full control over content typography.
 *   - `placement` ∈ {"bottom", "top", "auto"} — default "auto" picks
 *     bottom unless it would clip.
 *
 * NOT a Radix replacement — intentionally minimal so the bundle stays
 * lean. If we need rich keyboard nav across multiple tooltips later,
 * we'll swap in `@radix-ui/react-tooltip` site-wide.
 */
export default function PortalTooltip({
  testid,
  ariaLabel,
  trigger,
  panel,
  width = 256,
  placement = "auto",
  panelClassName = "bg-slate-950 text-white p-3 shadow-2xl border border-slate-800",
  triggerClassName = "inline-flex items-center justify-center w-3.5 h-3.5 text-slate-500 hover:text-[#10B981] transition-colors",
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  // Hover/focus open + leave/blur close so the tooltip behaves like a
  // native title attribute for users who don't tap.
  const handlers = {
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
  };

  return (
    <span className="relative inline-block align-middle" {...handlers}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        data-testid={testid}
        className={triggerClassName}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((s) => !s);
        }}
      >
        {trigger ?? <Info className="w-3.5 h-3.5" strokeWidth={2.5} />}
      </button>
      {open && (
        <PortalPanel
          anchorRef={triggerRef}
          onClose={() => setOpen(false)}
          testid={`${testid}-panel`}
          width={width}
          placement={placement}
          className={panelClassName}
        >
          {panel}
        </PortalPanel>
      )}
    </span>
  );
}

function PortalPanel({
  anchorRef,
  onClose,
  testid,
  width,
  placement,
  className,
  children,
}) {
  const panelRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });

  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    const place = () => {
      const a = anchorRef.current.getBoundingClientRect();
      const popW = width;
      const popH = panelRef.current?.offsetHeight || 120;
      const margin = 8;
      const wantBottom = placement !== "top";
      const fitsBottom = a.bottom + popH + 8 < window.innerHeight - margin;
      const fitsTop = a.top - popH - 8 > margin;
      let top;
      if (wantBottom && (placement === "bottom" || fitsBottom || !fitsTop)) {
        top = a.bottom + 8;
      } else {
        top = Math.max(margin, a.top - popH - 8);
      }
      let left = a.left + a.width / 2 - popW / 2;
      left = Math.min(Math.max(margin, left), window.innerWidth - popW - margin);
      setCoords({ top, left, ready: true });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef, width, placement]);

  useEffect(() => {
    const onClick = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      role="tooltip"
      data-testid={testid}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        width,
        opacity: coords.ready ? 1 : 0,
        transition: "opacity 120ms ease-out",
        zIndex: 1000,
      }}
      className={className}
    >
      {children}
    </div>,
    document.body,
  );
}
